import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { NestedFeed } from './NestedFeed';
import '../styles/Negotiation.css';
import Alert from './Alert';

const LEGACY_FIELD_IDS = {
    meds: '753',
    notes: '762'
};

const NEGOTIATION_TEXT_FIELD_NAMES = {
    threePartyDemand: '3P Last Demand/Date',
    threePartyOffer: '3P Last Offer/Date',
    firstPartyDemand: '1P Last Demand/Date',
    firstPartyOffer: '1P Last Offer/Date'
};

const resolveNegotiationFieldConfig = (fields = []) => {
    const fieldByName = new Map(
        (Array.isArray(fields) ? fields : []).map((field) => [String(field?.name || '').trim().toLowerCase(), String(field?.id || '')])
    );

    const getFieldIdByName = (name) => fieldByName.get(String(name || '').trim().toLowerCase()) || null;

    return {
        medsField: LEGACY_FIELD_IDS.meds,
        notesField: LEGACY_FIELD_IDS.notes,
        partyRows: [
            {
                label: '3P',
                demandField: getFieldIdByName(NEGOTIATION_TEXT_FIELD_NAMES.threePartyDemand),
                offerField: getFieldIdByName(NEGOTIATION_TEXT_FIELD_NAMES.threePartyOffer)
            },
            {
                label: '1P',
                demandField: getFieldIdByName(NEGOTIATION_TEXT_FIELD_NAMES.firstPartyDemand),
                offerField: getFieldIdByName(NEGOTIATION_TEXT_FIELD_NAMES.firstPartyOffer)
            }
        ]
    };
};

const buildEmptyState = (fieldConfig) => {
    const trackedFieldIds = [
        fieldConfig.medsField,
        fieldConfig.notesField,
        ...fieldConfig.partyRows.flatMap((party) => [party.demandField, party.offerField])
    ].filter(Boolean);

    return trackedFieldIds.reduce((accumulator, fieldId) => {
        accumulator[fieldId] = '';
        return accumulator;
    }, {});
};

const buildNegotiationState = (fieldUpdates, groupedFieldUpdates, emptyState, trackedFieldIds = []) => {
    const latestGroupedEntry = Array.isArray(groupedFieldUpdates)
        ? [...groupedFieldUpdates]
            .sort((left, right) => Number(right?.group_id || 0) - Number(left?.group_id || 0))[0]
        : null;

    const hasTrackedFlatValues = fieldUpdates && trackedFieldIds.some((fieldId) => (
        Object.prototype.hasOwnProperty.call(fieldUpdates, fieldId)
    ));

    if (hasTrackedFlatValues || latestGroupedEntry) {
        return {
            groupId: Number(latestGroupedEntry?.group_id || fieldUpdates?.group_id || 0) || null,
            values: {
                ...emptyState,
                ...(hasTrackedFlatValues ? fieldUpdates : {}),
                ...(latestGroupedEntry || {}),
            },
        };
    }

    return {
        groupId: null,
        values: { ...emptyState },
    };
};

const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') {
        return '—';
    }

    const parsed = Number(value);
    if (Number.isNaN(parsed)) {
        return value;
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 2
    }).format(parsed);
};

const formatDate = (value) => {
    if (!value) {
        return '—';
    }

    const date = new Date(`${value}T00:00:00`);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleDateString('en-US');
};

const summarizeTextValue = (value) => {
    const cleaned = String(value || '')
        .replace(/\r?\n+/g, '; ')
        .trim();
    return cleaned || '—';
};

const buildSummaryItems = (group, fieldConfig) => ([
    { label: 'Meds', value: formatCurrency(group[fieldConfig.medsField]) },
    { label: '3P Demand', value: summarizeTextValue(group[fieldConfig.partyRows[0]?.demandField]) },
    { label: '3P Offer', value: summarizeTextValue(group[fieldConfig.partyRows[0]?.offerField]) },
    { label: '1P Demand', value: summarizeTextValue(group[fieldConfig.partyRows[1]?.demandField]) },
    { label: '1P Offer', value: summarizeTextValue(group[fieldConfig.partyRows[1]?.offerField]) }
]);

const buildSavePayload = (group, leadId, caseId, groupId, trackedFieldIds) => {
    const payload = {
        lead_id: leadId,
        case_id: caseId,
        field_values: trackedFieldIds.reduce((accumulator, fieldId) => {
            accumulator[fieldId] = group[fieldId] ?? '';
            return accumulator;
        }, {})
    };

    if (groupId) {
        payload.group_id = groupId;
    }

    return payload;
};

const resizeTextarea = (element, maxRows = 10) => {
    if (!element) {
        return;
    }

    const computedStyle = window.getComputedStyle(element);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
    const singleRowHeight = lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
    const maxHeight = (lineHeight * maxRows) + paddingTop + paddingBottom + borderTop + borderBottom;

    element.style.height = `${singleRowHeight}px`;
    const nextHeight = Math.min(element.scrollHeight, maxHeight);
    element.style.height = `${Math.max(nextHeight, singleRowHeight)}px`;
    element.style.overflowY = element.scrollHeight > maxHeight ? 'auto' : 'hidden';
};

export const Negotiation = ({ case_id, lead_id, section_id, fields = [], fieldUpdates, groupedFieldUpdates = [], onRefresh }) => {
    const fieldConfig = useMemo(() => resolveNegotiationFieldConfig(fields), [fields]);
    const trackedFieldIds = useMemo(() => [
        fieldConfig.medsField,
        fieldConfig.notesField,
        ...fieldConfig.partyRows.flatMap((party) => [party.demandField, party.offerField])
    ].filter(Boolean), [fieldConfig]);
    const emptyState = useMemo(() => buildEmptyState(fieldConfig), [fieldConfig]);
    const tableRef = useRef(null);
    const [dataUpdates, setDataUpdates] = useState({ ...emptyState });
    const [currentGroupId, setCurrentGroupId] = useState(null);
    const [message, setMessage] = useState(null);
    const [isExpanded, setIsExpanded] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const messageTimeoutRef = useRef(null);

    useEffect(() => {
        const nextState = buildNegotiationState(fieldUpdates, groupedFieldUpdates, emptyState, trackedFieldIds);
        setDataUpdates(nextState.values);
        setCurrentGroupId(nextState.groupId);
    }, [fieldUpdates, groupedFieldUpdates, emptyState, trackedFieldIds]);

    useEffect(() => {
        if (!tableRef.current) {
            return;
        }

        tableRef.current
            .querySelectorAll('.negotiation-cell-textarea, .negotiation-textarea')
            .forEach((textarea) => resizeTextarea(textarea));
    }, [dataUpdates, isExpanded]);

    const setSuccessMessage = (text) => {
        setMessage({ type: 'success', text });
        clearTimeout(messageTimeoutRef.current);
        messageTimeoutRef.current = setTimeout(() => setMessage(null), 3000);
    };

    const setErrorMessage = (text) => {
        setMessage({ type: 'error', text });
    };

    const updateField = (fieldId, value) => {
        setDataUpdates((previous) => ({ ...previous, [fieldId]: value }));
    };

    const handleValueFactorsInput = (event) => {
        resizeTextarea(event.target);
    };

    const handleNegotiationTextareaInput = (event) => {
        resizeTextarea(event.target);
    };

    const saveFields = async () => {
        try {
            setIsSaving(true);
            const response = await fetch('https://api.casedb.co/custom_fields.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(buildSavePayload(dataUpdates, lead_id, case_id, currentGroupId, trackedFieldIds)),
                credentials: 'include'
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.message || 'Failed to update fields');
            }

            if (result.group_id) {
                setCurrentGroupId(result.group_id);
            }

            if (typeof onRefresh === 'function') {
                await onRefresh();
            }

            setSuccessMessage('Negotiation updated successfully.');
        } catch (error) {
            console.error('Error updating negotiation:', error);
            setErrorMessage(error.message || 'Error updating fields');
        } finally {
            setIsSaving(false);
        }
    };

    const summaryItems = buildSummaryItems(dataUpdates, fieldConfig);
    const medsWidth = Math.max(String(dataUpdates[fieldConfig.medsField] || '').length * 12 + 30, 75);
    
    return (
        <div className='case-section'>
            <div className='toc-header'>
                <h2>Negotiation</h2>
                <div className='negotiation-header-actions'>
                    <button className='action' onClick={saveFields} disabled={isSaving}>
                        {isSaving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
            {message && (
                <Alert message={message.text} type={message.type} onClose={() => setMessage(null)} />
            )}
            <div className='negotiation-table-container'>
                <div className='negotiation-group-card'>
                    <button
                        className={`negotiation-toggle ${isExpanded ? 'open' : ''}`}
                        onClick={() => setIsExpanded((previous) => !previous)}
                        aria-label={isExpanded ? 'Collapse negotiation details' : 'Expand negotiation details'}
                        type='button'
                    >
                        <ChevronDown size={18} />
                    </button>

                    {!isExpanded && (
                        <div className='negotiation-summary-grid small-text'>
                            {summaryItems.map((item) => (
                                <div className='negotiation-summary-item' key={item.label}>
                                    <span className='subtext'>{item.label}</span>
                                    <strong>{item.value}</strong>
                                </div>
                            ))}
                            <div className='negotiation-summary-item negotiation-summary-wide'>
                                <span className='subtext'>Value Factors</span>
                                <strong>{dataUpdates[fieldConfig.notesField] ? dataUpdates[fieldConfig.notesField] : '—'}</strong>
                            </div>
                        </div>
                    )}

                    {isExpanded && (
                        <table className='negotiation-table' ref={tableRef}>
                            <thead>
                                <tr className='negotiation-main-row'>
                                    <th>Meds</th>
                                    <th>Last Demand/Date</th>
                                    <th>Last Offer/Date</th>
                                </tr>
                            </thead>
                            <tbody className='small-text'>
                                <tr>
                                    <td className='negotiation-field'>
                                        <span className='dollar-sign subtext'>$</span>
                                        <input
                                            className='negotiation-input'
                                            type='number'
                                            style={{ width: `${medsWidth}px`, padding: '0 15px' }}
                                            placeholder='0.00'
                                            value={dataUpdates[fieldConfig.medsField] || ''}
                                            onChange={(event) => updateField(fieldConfig.medsField, event.target.value)}
                                        />
                                    </td>
                                    <td colSpan='2' className='negotiation-detail-cell'>
                                        <table className='nested-negotiation-table'>
                                            <tbody>
                                                {fieldConfig.partyRows.map((party) => (
                                                    <tr key={party.label}>
                                                        <td>{party.label}</td>
                                                        <td className='negotiation-field'>
                                                            <textarea
                                                                className='negotiation-cell-textarea'
                                                                rows={1}
                                                                placeholder='Last Demand/Date'
                                                                value={party.demandField ? (dataUpdates[party.demandField] || '') : ''}
                                                                onChange={(event) => party.demandField && updateField(party.demandField, event.target.value)}
                                                                onInput={handleNegotiationTextareaInput}
                                                                disabled={!party.demandField}
                                                            />
                                                        </td>
                                                        <td className='negotiation-field'>
                                                            <textarea
                                                                className='negotiation-cell-textarea'
                                                                rows={1}
                                                                placeholder='Last Offer/Date'
                                                                value={party.offerField ? (dataUpdates[party.offerField] || '') : ''}
                                                                onChange={(event) => party.offerField && updateField(party.offerField, event.target.value)}
                                                                onInput={handleNegotiationTextareaInput}
                                                                disabled={!party.offerField}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                                <tr>
                                    <td className='negotiation-value-label'>Value Factors</td>
                                    <td colSpan='2'>
                                        <textarea
                                            className='negotiation-textarea'
                                            rows={1}
                                            placeholder='Enter value factors...'
                                            value={dataUpdates[fieldConfig.notesField] || ''}
                                            onChange={(event) => updateField(fieldConfig.notesField, event.target.value)}
                                            onInput={handleValueFactorsInput}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
            <NestedFeed case_id={case_id} section_id={section_id} />
        </div>
    );
};